import {
  TableRow,
  TableHead,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Typography,
  Tooltip,
  Button,
  useTheme,
} from '@mui/material';
import {type OptimizationRun, OptimizationRunStateEnum} from 'client';
import CopyIcon from '@mui/icons-material/ContentCopy';
import copy from 'copy-to-clipboard';
import Alert from 'helpers/Alert';

import GenericPaperTitle from 'components/Generic/PaperTitle';

import GenericPaper from 'components/Generic/Paper';
import {dateDisplay, secondsFormat} from 'helpers/dateUtils';
import {format} from 'date-fns';
import {URLParams, URLPathname} from 'interfaces';
import useParseURL from 'hooks/useParseURL';
import {EMPTY_ID, useGetOptimizationRuns, useGetPlans} from 'hooks/swr';

import {useTranslation} from 'react-i18next';
import {useState} from 'react';
import {type NavigateFunction} from 'react-router-dom';

export function AllRunsTable() {
  const theme = useTheme();
  const {t} = useTranslation('common');

  const {data: plans} = useGetPlans({resourceId: EMPTY_ID});

  const {data: allOptimizationRuns} = useGetOptimizationRuns({resourceId: EMPTY_ID});

  const ROW_SIZE: 'small' | 'medium' = 'small';
  const hasOptimizationAlgorithm = allOptimizationRuns?.some((run) => run.solutionAlgorithm);

  return (
    <GenericPaper>
      <GenericPaperTitle title={t('optimizationRuns')} p={2} />
      <TableContainer sx={{maxHeight: 800}}>
        <Table stickyHeader size={ROW_SIZE}>
          <TableHead>
            <TableRow>
              <TableCell>{t('created')}</TableCell>
              <TableCell>{t('started')}</TableCell>
              <TableCell>{t('updated')}</TableCell>
              <TableCell>{t('originalPlanName')}</TableCell>
              <TableCell>{t('originalPlanId')}</TableCell>
              <TableCell>{t('optimizedPlanId')}</TableCell>
              {hasOptimizationAlgorithm && <TableCell>{t('solutionAlgorithm')}</TableCell>}
              <TableCell>{t('duration')}</TableCell>
              <TableCell>{t('status')}</TableCell>
              <TableCell>{t('runId')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allOptimizationRuns &&
              plans &&
              allOptimizationRuns.map((optimizationRun) => (
                <Row
                  key={optimizationRun.id}
                  optimizationRun={optimizationRun}
                  hasOptimizationAlgorithm={hasOptimizationAlgorithm ?? false}
                />
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </GenericPaper>
  );
}

const Row = ({
  optimizationRun,
  hasOptimizationAlgorithm,
}: {
  optimizationRun: OptimizationRun;
  hasOptimizationAlgorithm: boolean;
}) => {
  const theme = useTheme();
  const {t} = useTranslation('common');

  const {navigate, urlSearchParams} = useParseURL();
  const [open, setOpen] = useState(false);

  const createdAt = formatDisplayTimestamp('createdAt', optimizationRun);
  const startedAt = formatDisplayTimestamp('startedAt', optimizationRun);
  const updatedAt = formatDisplayTimestamp('updatedAt', optimizationRun);

  const durationMinutes = optimizationRun.elapsedTime && secondsFormat(optimizationRun.elapsedTime);

  const ROW_SIZE: 'small' | 'medium' = 'small';
  const ROW_HEIGHT = 40;

  const stateColor = () => {
    switch (optimizationRun.state) {
      case OptimizationRunStateEnum.Pending:
      case OptimizationRunStateEnum.Started:
        return theme.palette.info.main;
      case OptimizationRunStateEnum.Failure:
        return theme.palette.error.main;
      case OptimizationRunStateEnum.Success:
        return theme.palette.success.main;
      case OptimizationRunStateEnum.Canceled:
        return theme.palette.warning.main;
      default:
        return undefined;
    }
  };

  const navigateTo = () => {
    switch (optimizationRun.state) {
      case OptimizationRunStateEnum.Pending:
      case OptimizationRunStateEnum.Started:
        return false;
      default:
        return true;
    }
  };

  const state = t(optimizationRun.state);

  return (
    <TableRow
      hover
      style={{cursor: 'pointer'}}
      onClick={() => navigateTo() && navigateToOptimizationRun(optimizationRun.id, navigate, urlSearchParams)}
    >
      <TableCell size={ROW_SIZE} height={ROW_HEIGHT}>
        {createdAt}
      </TableCell>
      <TableCell size={ROW_SIZE} height={ROW_HEIGHT}>
        {startedAt}
      </TableCell>
      <TableCell size={ROW_SIZE} height={ROW_HEIGHT}>
        {updatedAt}
      </TableCell>
      <TableCell size={ROW_SIZE} height={ROW_HEIGHT}>
        {optimizationRun.planName}
      </TableCell>
      <TableCell size={ROW_SIZE} height={ROW_HEIGHT}>
        {optimizationRun.planId}
      </TableCell>
      <TableCell size={ROW_SIZE} height={ROW_HEIGHT}>
        {optimizationRun.optimizedPlanId}
      </TableCell>
      {hasOptimizationAlgorithm && (
        <TableCell size={ROW_SIZE} height={ROW_HEIGHT}>
          {optimizationRun.solutionAlgorithm}
        </TableCell>
      )}
      <TableCell size={ROW_SIZE} height={ROW_HEIGHT}>
        {durationMinutes !== undefined && `${durationMinutes} ${t('minutes')}`}
      </TableCell>
      <TableCell size={ROW_SIZE} height={ROW_HEIGHT} sx={{color: stateColor()}}>
        {state}
      </TableCell>
      <TableCell size={ROW_SIZE} height={ROW_HEIGHT} sx={{py: 0}}>
        <Tooltip
          PopperProps={{
            disablePortal: true,
          }}
          onClose={() => {
            setOpen(false);
          }}
          open={open}
          disableInteractive
          title={t('copied') ?? ''}
        >
          <Button
            variant="text"
            color="inherit"
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              copy(optimizationRun.id);
              setOpen(true);
              Alert.success(t('copiedToClipboard'));
            }}
            endIcon={<CopyIcon fontSize="inherit" />}
          >
            <Typography variant="monospaced" sx={{textTransform: 'none'}}>
              {optimizationRun.id}
            </Typography>
          </Button>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

const formatDisplayTimestamp = (dateString: keyof OptimizationRun, optimizationRun: OptimizationRun) => {
  const dateTime = dateString in optimizationRun && new Date(optimizationRun[dateString] as string);
  return dateTime && `${dateDisplay(dateTime)} ${format(dateTime, 'HH:mm')}`;
};

export const navigateToOptimizationRun = (
  runId: string,
  navigate: NavigateFunction,
  urlSearchParams: URLSearchParams,
) => {
  urlSearchParams.delete(URLParams.run);
  urlSearchParams.append(URLParams.run, runId);
  navigate({
    pathname: URLPathname.optimizationRunDetail,
    search: urlSearchParams.toString(),
  });
};
